import { createColumnHelper, type Row } from '@tanstack/react-table'
import { useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useQuery } from 'react-query'
import { fetchInvoiceHeadersReport } from '../../../../api/newApi'
import { useChosenCompany, useUser } from '../../../../context/UserProvider'
import type { InvoiceHeader } from '../../../../types'
import GenericTable from '../../../cars/components/V3/_shared/GenericTable/GenericTable'
import { TableMobileCardContent } from '../../../cars/components/V3/_shared/GenericTable/TableMobileCardContent'
import { Input } from '../../../cars/components/V3/_shared/Form/Input'
import { SearchIcon } from '../../../cars/components/V3/SearchIcon'
import { exportToExcel } from '../components/ExcelExporter'
import Excel from '../../../../icons/Excel'
import { ReportTextCell } from '../components/ReportTextCell'
import { formatCurrency } from '../utils/formatCurrency'

const InvoiceReportHeaders = () => {
  const { consolidatedInvoiceNo } = useParams()
  const navigate = useNavigate()
  const companyId = useChosenCompany()?.id
  const contactId = useUser()?.user?.id
  const [search, setSearch] = useState('')

  const { data: response, status } = useQuery(
    ['invoiceHeadersReport', companyId, contactId, consolidatedInvoiceNo],
    () => fetchInvoiceHeadersReport({ companyId, contactId, consolidatedInvoiceNo, fetchAll: true }),
    {
      enabled: !!companyId && !!contactId && !!consolidatedInvoiceNo,
    },
  )

  const items = (response && 'items' in response ? response.items : []) as InvoiceHeader[]
  const filteredItems = useMemo(() => {
    const normalizedSearch = search.trim().toLowerCase()
    if (!normalizedSearch) return items

    return items.filter((item) => {
      const searchableFields = [
        item.invoiceNo,
        item.accountNo,
        mapCollectionOnly(item.collectionOnly),
        item.consolidateInvoiceCode,
        item.invoiceGrouping,
        item.mainCustomerNo,
        item.driverName,
        item.invoiceGroupDescription,
        item.description,
        item.regNo,
        item.invoiceAmount,
        item.invoiceAmountInclVat,
      ]

      return searchableFields.some((value) =>
        String(value || '')
          .toLowerCase()
          .includes(normalizedSearch),
      )
    })
  }, [items, search])

  const handleExport = () => {
    const excelData = filteredItems.map((item) => ({
      ...item,
      collectionOnly: mapCollectionOnly(item.collectionOnly),
    }))

    exportToExcel({
      data: excelData,
      filename: `Fakturalinjer_${consolidatedInvoiceNo}.xlsx`,
      sheetName: 'Fakturalinjer',
      columns: invoiceHeaderExcelColumns,
    })
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-2">
        <div className="flex flex-col gap-1">
          <span className="text-xl font-medium text-primaryText">Fakturalinjer</span>
          <span className="text-sm text-secondaryText">Samlefaktura {consolidatedInvoiceNo}</span>
        </div>
        <div className="flex flex-row gap-2 items-center justify-between">
          <div className="relative w-full md:w-80">
            <Input
              id="invoice-header-search"
              type="search"
              value={search}
              placeholder="Søk"
              className="h-11 pl-11"
              onChange={(event) => setSearch(event.target.value)}
            />
            <div className="absolute top-[14px] left-[14px]">
              <SearchIcon />
            </div>
          </div>
          <button
            type="button"
            className="h-11 w-fit gap-2 bg-blueDark text-interaction hover:text-interactionHover transition-all ease-in-out flex items-center px-3 py-2 rounded-sm disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={filteredItems.length === 0}
            onClick={handleExport}
          >
            <span className="scale-75">
              <Excel />
            </span>
            <span>Eksporter</span>
          </button>
        </div>
      </div>

      <GenericTable
        className="max-h-[65vh]"
        data={filteredItems}
        isLoading={status === 'loading'}
        columns={columns}
        onRowClick={(row) => {
          navigate(`/reports/invoice/${consolidatedInvoiceNo}/${encodeURIComponent(row.original.invoiceNo)}`)
        }}
        mobileCardContent={(row) => <MobileCardContent row={row as Row<InvoiceHeader>} />}
      />
    </div>
  )
}

export default InvoiceReportHeaders

function MobileCardContent({ row }: { row: Row<InvoiceHeader> }) {
  const navigate = useNavigate()
  const header = `Faktura - ${row.original.invoiceNo}`
  const data = columns.map((column) => {
    const id = column.id as keyof InvoiceHeader
    const value =
      id === 'collectionOnly'
        ? mapCollectionOnly(row.original[id])
        : id === 'invoiceAmount' || id === 'invoiceAmountInclVat'
          ? formatCurrency(row.original[id] as number)
          : row.original[id]

    return {
      label: String(column.header),
      value: value || '',
    }
  })
  const action = {
    type: 'chevron' as const,
    onClick: () =>
      navigate(`/reports/invoice/${row.original.consolidateInvoiceNo}/${encodeURIComponent(row.original.invoiceNo)}`),
  }

  return <TableMobileCardContent header={header} data={data} action={action} />
}

const columnHelper = createColumnHelper<InvoiceHeader>()

const columns = [
  columnHelper.accessor('invoiceNo', {
    id: 'invoiceNo',
    header: 'Fakturanr',
    cell: (row) => <ReportTextCell value={row.getValue()} />,
    size: 90,
  }),
  columnHelper.accessor('accountNo', {
    id: 'accountNo',
    header: 'Kontrakt nr',
    size: 100,
    cell: (row) => <ReportTextCell value={row.getValue()} />,
  }),
  columnHelper.accessor('collectionOnly', {
    id: 'collectionOnly',
    header: 'Gruppe',
    enableSorting: false,
    cell: (row) => <ReportTextCell value={mapCollectionOnly(row.getValue())} />,
  }),
  columnHelper.accessor('consolidateInvoiceCode', {
    id: 'consolidateInvoiceCode',
    header: 'Samlefaktura kode',
    enableSorting: false,
    size: 155,
    cell: (row) => <ReportTextCell value={row.getValue()} />,
  }),
  columnHelper.accessor('invoiceGrouping', {
    id: 'invoiceGrouping',
    header: 'Fakturagruppe',
    cell: (row) => <ReportTextCell value={row.getValue()} />,
  }),
  columnHelper.accessor('mainCustomerNo', {
    id: 'mainCustomerNo',
    header: 'Kundenr',
    cell: (row) => <ReportTextCell value={row.getValue()} />,
  }),
  columnHelper.accessor('driverName', {
    id: 'driverName',
    header: 'Sjåfør',
    enableSorting: false,
    cell: (row) => <ReportTextCell value={row.getValue()} />,
  }),
  columnHelper.accessor('invoiceGroupDescription', {
    id: 'invoiceGroupDescription',
    header: 'Beskrivelse',
    enableSorting: false,
    cell: (row) => <ReportTextCell value={row.getValue()} />,
  }),
  columnHelper.accessor('regNo', {
    id: 'regNo',
    header: 'Regnr',
    enableSorting: false,
    cell: (row) => <ReportTextCell value={row.getValue()} />,
  }),
  columnHelper.accessor('invoiceAmount', {
    id: 'invoiceAmount',
    header: 'Beløp eks. mva',
    size: 125,
    meta: {
      alignmentType: 'right',
      mobileFooterLabel: 'Sum beløp eks. mva',
    },
    cell: (row) => formatCurrency(row.getValue()) || '',
    footer: ({ table }) => {
      const total = table
        .getFilteredRowModel()
        .rows.reduce((sum, row) => sum + ((row.getValue('invoiceAmount') as number) || 0), 0)
      return formatCurrency(total)
    },
  }),
  columnHelper.accessor('invoiceAmountInclVat', {
    id: 'invoiceAmountInclVat',
    header: 'Beløp inkl. mva',
    size: 125,
    meta: {
      alignmentType: 'right',
      mobileFooterLabel: 'Sum beløp inkl. mva',
    },
    cell: (row) => formatCurrency(row.getValue()) || '',
    footer: ({ table }) => {
      const total = table
        .getFilteredRowModel()
        .rows.reduce((sum, row) => sum + ((row.getValue('invoiceAmountInclVat') as number) || 0), 0)
      return formatCurrency(total)
    },
  }),
]

const invoiceHeaderExcelColumns = [
  { key: 'invoiceNo', header: 'Fakturanr', width: 15 },
  { key: 'accountNo', header: 'Kontrakt nr', width: 15 },
  { key: 'collectionOnly', header: 'Gruppe', width: 20 },
  { key: 'consolidateInvoiceCode', header: 'Samlefaktura kode', width: 18 },
  { key: 'invoiceGrouping', header: 'Fakturagruppe', width: 18 },
  { key: 'mainCustomerNo', header: 'Kundenr', width: 15 },
  { key: 'driverName', header: 'Sjåfør', width: 30 },
  { key: 'invoiceGroupDescription', header: 'Beskrivelse', width: 40 },
  { key: 'regNo', header: 'Regnr', width: 15 },
  { key: 'invoiceAmount', header: 'Beløp eks. mva', width: 18 },
  { key: 'invoiceAmountInclVat', header: 'Beløp inkl. mva', width: 18 },
]

const mapCollectionOnly = (group: string) => {
  switch (group) {
    case '1':
      return 'LEIE / FORSIKRING'
    case '0':
      return 'BILHOLD'
    default:
      return 'UKJENT'
  }
}
